import React, { useEffect, useRef, useState } from 'react';
import {
  Box, VStack, HStack, FormControl, FormLabel, Input, Checkbox, Text, Heading, SimpleGrid, Button, Alert, AlertIcon, Flex, Radio, RadioGroup, Stack, Spinner, Link, Icon,
} from '@chakra-ui/react';
import { FaExternalLinkAlt } from 'react-icons/fa';
import SignatureCanvas from 'react-signature-canvas';
import { buildW9BlobUrl } from '../../utils/fillW9Pdf';

// Converts a data: URL PNG signature into Uint8Array bytes for pdf-lib.
function dataUrlToBytes(dataUrl) {
  if (!dataUrl) return null;
  const base64 = dataUrl.split(',')[1] || '';
  const bin = atob(base64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes;
}

// Visually mimics IRS Form W-9 (Rev. March 2024) so phlebs recognize what they're filling out.
// Field IDs map to IRS box numbers where possible.

const CLASSIFICATIONS = [
  { value: 'individual',   label: 'Individual/sole proprietor or single-member LLC' },
  { value: 'c_corp',       label: 'C corporation' },
  { value: 's_corp',       label: 'S corporation' },
  { value: 'partnership',  label: 'Partnership' },
  { value: 'trust_estate', label: 'Trust/estate' },
];

function SectionHeader({ num, title }) {
  return (
    <HStack spacing={2} mb={2} align="baseline">
      <Box bg="gray.700" color="white" w="22px" h="22px" borderRadius="sm" display="flex" alignItems="center" justifyContent="center" fontSize="xs" fontWeight="700">
        {num}
      </Box>
      <Text fontSize="sm" fontWeight="700" color="gray.800" textTransform="uppercase" letterSpacing="0.03em">{title}</Text>
    </HStack>
  );
}

function FormBox({ children, ...props }) {
  return (
    <Box borderWidth="1px" borderColor="gray.300" borderRadius="md" p={4} bg="white" {...props}>
      {children}
    </Box>
  );
}

// Renders SSN / EIN as boxed digit groups, like the real form.
function TinBoxes({ value, onChange, type }) {
  // SSN = 3-2-4 (9 digits), EIN = 2-7 (9 digits)
  const groups = type === 'ein' ? [2, 7] : [3, 2, 4];
  const total = groups.reduce((a, b) => a + b, 0);
  const inputsRef = useRef([]);

  const digitsArr = React.useMemo(() => {
    const clean = (value || '').replace(/\D/g, '').slice(0, total);
    return Array.from({ length: total }, (_, i) => clean[i] || '');
  }, [value, total]);

  const writeDigits = (arr) => {
    onChange(arr.join('').replace(/\s/g, ''));
  };

  const focusAt = (i) => {
    const el = inputsRef.current[i];
    if (el) { el.focus(); el.select?.(); }
  };

  const handleChange = (i, raw) => {
    const cleaned = raw.replace(/\D/g, '');
    if (!cleaned) {
      const next = [...digitsArr]; next[i] = ''; writeDigits(next);
      return;
    }
    // Support paste: spread the digits across remaining boxes.
    const next = [...digitsArr];
    for (let k = 0; k < cleaned.length && i + k < total; k++) {
      next[i + k] = cleaned[k];
    }
    writeDigits(next);
    const nextFocus = Math.min(i + cleaned.length, total - 1);
    focusAt(nextFocus);
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      if (!digitsArr[i] && i > 0) {
        e.preventDefault();
        const next = [...digitsArr]; next[i - 1] = ''; writeDigits(next);
        focusAt(i - 1);
      }
    } else if (e.key === 'ArrowLeft' && i > 0) {
      e.preventDefault(); focusAt(i - 1);
    } else if (e.key === 'ArrowRight' && i < total - 1) {
      e.preventDefault(); focusAt(i + 1);
    }
  };

  let idx = 0;
  return (
    <HStack spacing={2}>
      {groups.map((len, gi) => (
        <React.Fragment key={gi}>
          {gi > 0 && <Text fontSize="lg" color="gray.500" fontWeight="700">-</Text>}
          <HStack spacing={1}>
            {Array.from({ length: len }).map(() => {
              const i = idx++;
              return (
                <Input
                  key={i}
                  ref={(el) => { inputsRef.current[i] = el; }}
                  value={digitsArr[i]}
                  onChange={(e) => handleChange(i, e.target.value)}
                  onKeyDown={(e) => handleKeyDown(i, e)}
                  onFocus={(e) => e.target.select()}
                  inputMode="numeric"
                  type="password"
                  maxLength={1}
                  textAlign="center"
                  w="32px"
                  h="36px"
                  px={0}
                  fontFamily="mono"
                  fontSize="md"
                  borderWidth="1px"
                  borderColor="gray.400"
                  borderRadius="sm"
                  _focus={{ borderColor: 'blue.400', boxShadow: '0 0 0 1px var(--chakra-colors-blue-400)' }}
                />
              );
            })}
          </HStack>
        </React.Fragment>
      ))}
    </HStack>
  );
}

export default function W9Form({ value, onChange }) {
  const sigPad = useRef();
  const [signed, setSigned] = useState(!!value.signature);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfError, setPdfError] = useState(null);
  const [pdfBuilding, setPdfBuilding] = useState(false);
  const lastBlobRef = useRef(null);

  const set = (patch) => onChange({ ...value, ...patch });
  const tinType = value.tin_type || 'ssn';

  // Live-preview the actual IRS W-9 PDF with the entered data, debounced so we
  // don't re-render the PDF on every keystroke. The PDF lives at
  // /public/forms/fw9.pdf and is filled via pdf-lib in src/utils/fillW9Pdf.js.
  useEffect(() => {
    let cancelled = false;
    const timer = setTimeout(async () => {
      setPdfBuilding(true);
      try {
        const sigBytes = value.signature ? dataUrlToBytes(value.signature) : null;
        const url = await buildW9BlobUrl(value, sigBytes);
        if (cancelled) { URL.revokeObjectURL(url); return; }
        // Release prior blob URL.
        if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current);
        lastBlobRef.current = url;
        setPdfUrl(url);
        setPdfError(null);
      } catch (e) {
        if (!cancelled) setPdfError(e.message || 'Could not build PDF preview');
      } finally {
        if (!cancelled) setPdfBuilding(false);
      }
    }, 500);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [value]);

  // Clean up blob URL on unmount.
  useEffect(() => () => { if (lastBlobRef.current) URL.revokeObjectURL(lastBlobRef.current); }, []);

  const handleEndSign = () => {
    if (!sigPad.current) return;
    const dataUrl = sigPad.current.getCanvas().toDataURL('image/png');
    set({ signature: dataUrl });
    setSigned(!sigPad.current.isEmpty());
  };

  const clearSig = () => {
    if (sigPad.current) sigPad.current.clear();
    set({ signature: null });
    setSigned(false);
  };

  const isLlc = value.tax_classification === 'llc';
  const isOther = value.tax_classification === 'other';

  return (
    <VStack align="stretch" spacing={4}>
      {/* Live PDF preview — the actual IRS W-9 filled with the user's data. */}
      <Box borderWidth="2px" borderColor="blue.200" borderRadius="md" overflow="hidden" bg="gray.100">
        <HStack justify="space-between" px={3} py={2} bg="blue.50" borderBottomWidth="1px" borderColor="blue.200">
          <HStack spacing={2}>
            <Text fontSize="xs" fontWeight="700" color="blue.700" textTransform="uppercase" letterSpacing="0.05em">
              Live W-9 Preview
            </Text>
            {pdfBuilding && <Spinner size="xs" color="blue.500" />}
          </HStack>
          {pdfUrl && (
            <Link href={pdfUrl} isExternal fontSize="xs" color="blue.600">
              Open full size <Icon as={FaExternalLinkAlt} boxSize={2.5} mb={0.5} />
            </Link>
          )}
        </HStack>
        {pdfError ? (
          <Alert status="error" fontSize="xs" borderRadius="0">
            <AlertIcon />
            {pdfError} — fall back to the fields below; we'll still record your data.
          </Alert>
        ) : pdfUrl ? (
          <Box as="iframe" src={pdfUrl + '#toolbar=0&navpanes=0&zoom=page-width'} title="W-9 preview" w="100%" h="650px" border="0" />
        ) : (
          <Box h="650px" display="flex" alignItems="center" justifyContent="center"><Spinner /></Box>
        )}
      </Box>

      <Text fontSize="xs" color="gray.500" textAlign="center" fontStyle="italic">
        Fill the fields below and the actual IRS Form W-9 above will update in real time.
      </Text>

      {/* Form header — mimics the IRS Form W-9 masthead */}
      <Box bg="gray.50" borderWidth="2px" borderColor="gray.700" borderRadius="md" p={4}>
        <Flex justify="space-between" align="start" gap={3}>
          <Box>
            <Text fontSize="2xs" color="gray.500" fontWeight="600">Form W-9</Text>
            <Text fontSize="2xs" color="gray.500">(Rev. March 2024)</Text>
            <Text fontSize="2xs" color="gray.500">Department of the Treasury</Text>
            <Text fontSize="2xs" color="gray.500">Internal Revenue Service</Text>
          </Box>
          <Box textAlign="center" flex={1}>
            <Heading size="md" color="gray.800">Request for Taxpayer</Heading>
            <Heading size="md" color="gray.800">Identification Number and Certification</Heading>
            <Text fontSize="xs" color="gray.600" mt={2} fontStyle="italic">
              Give Form to the requester. Do not send to the IRS.
            </Text>
          </Box>
        </Flex>
      </Box>

      <Alert status="info" fontSize="xs" borderRadius="md">
        <AlertIcon />
        At Home Blood Draw needs this form on file to pay you as a 1099 contractor and report
        your earnings to the IRS at year-end. Your TIN is encrypted and never shared.
      </Alert>

      {/* Part I — identification */}
      <FormBox>
        <SimpleGrid columns={1} spacing={3}>
          <Box>
            <SectionHeader num="1" title="Name of entity/individual (as shown on your income tax return)" />
            <Input id="f-w9-legal_name" size="sm" value={value.legal_name || ''} onChange={(e) => set({ legal_name: e.target.value })} />
          </Box>
          <Box>
            <SectionHeader num="2" title="Business name/disregarded entity name, if different from above" />
            <Input size="sm" value={value.business_name || ''} onChange={(e) => set({ business_name: e.target.value })} />
          </Box>

          <Box id="f-w9-tax_classification">
            <SectionHeader num="3a" title="Check appropriate box for federal tax classification" />
            <VStack align="stretch" spacing={2} pl={1}>
              {CLASSIFICATIONS.map((c) => (
                <Checkbox
                  key={c.value}
                  isChecked={value.tax_classification === c.value}
                  onChange={() => set({ tax_classification: c.value })}
                  colorScheme="blue"
                  size="sm"
                >
                  {c.label}
                </Checkbox>
              ))}
              <HStack spacing={2}>
                <Checkbox
                  isChecked={isLlc}
                  onChange={() => set({ tax_classification: 'llc' })}
                  colorScheme="blue"
                  size="sm"
                >
                  Limited liability company. Enter the tax classification (C = C corporation, S = S corporation, P = partnership) →
                </Checkbox>
                <Input
                  size="sm"
                  maxLength={1}
                  w="40px"
                  textAlign="center"
                  fontFamily="mono"
                  value={value.llc_classification || ''}
                  onChange={(e) => set({ llc_classification: e.target.value.toUpperCase().replace(/[^CSP]/g, ''), tax_classification: 'llc' })}
                  isDisabled={!isLlc}
                />
              </HStack>
              <HStack spacing={2} align="center">
                <Checkbox
                  isChecked={isOther}
                  onChange={() => set({ tax_classification: 'other' })}
                  colorScheme="blue"
                  size="sm"
                >
                  Other (see instructions) →
                </Checkbox>
                <Input
                  size="sm"
                  flex={1}
                  value={value.other_classification || ''}
                  onChange={(e) => set({ other_classification: e.target.value, tax_classification: 'other' })}
                  isDisabled={!isOther}
                />
              </HStack>
            </VStack>
          </Box>

          <Box>
            <SectionHeader num="4" title="Exemptions (codes apply only to certain entities, not individuals)" />
            <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>Exempt payee code (if any)</FormLabel>
                <Input size="sm" maxLength={2} value={value.exempt_payee_code || ''} onChange={(e) => set({ exempt_payee_code: e.target.value })} />
              </FormControl>
              <FormControl>
                <FormLabel fontSize="xs" mb={1}>FATCA reporting code (if any)</FormLabel>
                <Input size="sm" maxLength={2} value={value.fatca_code || ''} onChange={(e) => set({ fatca_code: e.target.value })} />
              </FormControl>
            </SimpleGrid>
          </Box>

          <Box>
            <SectionHeader num="5" title="Address (number, street, and apt. or suite no.)" />
            <Input id="f-w9-address" size="sm" value={value.address || ''} onChange={(e) => set({ address: e.target.value })} />
          </Box>

          <Box>
            <SectionHeader num="6" title="City, state, and ZIP code" />
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={2}>
              <Input id="f-w9-city" size="sm" placeholder="City" value={value.city || ''} onChange={(e) => set({ city: e.target.value })} />
              <Input id="f-w9-state" size="sm" placeholder="State" maxLength={2} value={value.state || ''} onChange={(e) => set({ state: e.target.value.toUpperCase() })} />
              <Input id="f-w9-zip" size="sm" placeholder="ZIP" value={value.zip || ''} onChange={(e) => set({ zip: e.target.value })} />
            </SimpleGrid>
          </Box>

          <Box>
            <SectionHeader num="7" title="List account number(s) here (optional)" />
            <Input size="sm" value={value.account_numbers || ''} onChange={(e) => set({ account_numbers: e.target.value })} />
          </Box>
        </SimpleGrid>
      </FormBox>

      {/* Part II — TIN */}
      <FormBox>
        <Text fontSize="sm" fontWeight="700" color="gray.800" textTransform="uppercase" letterSpacing="0.03em" mb={3}>
          Part I — Taxpayer Identification Number (TIN)
        </Text>
        <Text fontSize="xs" color="gray.600" mb={3} lineHeight="1.6">
          Enter your TIN in the appropriate box. The TIN provided must match the name given on line 1
          to avoid backup withholding. For individuals, this is generally your social security number
          (SSN). For entities, it is your employer identification number (EIN).
        </Text>

        <RadioGroup value={tinType} onChange={(v) => set({ tin_type: v, tin: '' })} mb={3}>
          <Stack direction="row" spacing={6}>
            <Radio value="ssn">Social security number</Radio>
            <Radio value="ein">Employer identification number</Radio>
          </Stack>
        </RadioGroup>

        <Box bg="gray.50" borderWidth="1px" borderColor="gray.300" borderRadius="md" p={3}>
          <Text fontSize="2xs" color="gray.500" mb={2} fontWeight="600">{tinType === 'ssn' ? 'SOCIAL SECURITY NUMBER' : 'EMPLOYER IDENTIFICATION NUMBER'}</Text>
          <Box id="f-w9-tin"><TinBoxes type={tinType} value={value.tin || ''} onChange={(v) => set({ tin: v })} /></Box>
        </Box>
      </FormBox>

      {/* Part III — certification */}
      <FormBox>
        <Text fontSize="sm" fontWeight="700" color="gray.800" textTransform="uppercase" letterSpacing="0.03em" mb={3}>
          Part II — Certification
        </Text>
        <Text fontSize="xs" color="gray.600" lineHeight="1.6" mb={3}>
          Under penalties of perjury, I certify that:
        </Text>
        <VStack align="stretch" spacing={1.5} pl={3} mb={3}>
          <Text fontSize="xs" color="gray.700">1. The number shown on this form is my correct taxpayer identification number (or I am waiting for a number to be issued to me); and</Text>
          <Text fontSize="xs" color="gray.700">2. I am not subject to backup withholding because (a) I am exempt from backup withholding, (b) I have not been notified by the IRS that I am subject to backup withholding as a result of a failure to report all interest or dividends, or (c) the IRS has notified me that I am no longer subject to backup withholding; and</Text>
          <Text fontSize="xs" color="gray.700">3. I am a U.S. citizen or other U.S. person; and</Text>
          <Text fontSize="xs" color="gray.700">4. The FATCA code(s) entered on this form (if any) indicating that I am exempt from FATCA reporting is correct.</Text>
        </VStack>

        <Checkbox
          id="f-w9-certification"
          isChecked={!!value.certification}
          onChange={(e) => set({ certification: e.target.checked })}
          colorScheme="blue"
          mb={4}
        >
          I have read and agree to the certification above.
        </Checkbox>

        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
          <Box id="f-w9-signature">
            <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1.5}>Sign here ▸</Text>
            <Box borderWidth="2px" borderColor="gray.400" borderRadius="md" p={2} bg="white">
              <SignatureCanvas
                penColor="black"
                canvasProps={{ width: 400, height: 90, className: 'sigCanvas', style: { width: '100%', height: '90px' } }}
                ref={sigPad}
                onEnd={handleEndSign}
              />
            </Box>
            <HStack mt={2}>
              <Button size="xs" onClick={clearSig}>Clear signature</Button>
              {signed && <Text fontSize="xs" color="green.500">✓ Signed</Text>}
            </HStack>
          </Box>
          <Box>
            <Text fontSize="xs" fontWeight="600" color="gray.600" mb={1.5}>Date ▸</Text>
            <Input
              size="sm"
              type="date"
              value={value.signed_date || new Date().toISOString().split('T')[0]}
              onChange={(e) => set({ signed_date: e.target.value })}
            />
          </Box>
        </SimpleGrid>
      </FormBox>
    </VStack>
  );
}

export function isW9Complete(w9) {
  if (!w9) return false;
  return Boolean(
    w9.legal_name && w9.tax_classification && w9.address && w9.city && w9.state && w9.zip &&
    w9.tin && w9.tin.length === 9 && w9.certification && w9.signature
  );
}
